// =============================================================================
// ui/state/practice.tsx — the Fix-it gym's sessions and their copied trades. #59.
// -----------------------------------------------------------------------------
// Same shape as `backtests.tsx`, which is deliberate: Practice IS Backtesting
// with a different question, and the two data layers mirror each other on
// purpose (see `docs/PRACTICE_PLAN.md` and `io/sync/practice.ts`).
//
// ── ⚠ TWO READS, ONE PROVIDER ──
//
// Sessions and their trades are separate tables but never useful apart — a
// session with no trades is a heading, and a trade with no session has nowhere
// to render. Loading them together means a screen cannot show one before the
// other and briefly claim a session is empty when it is not.
//
// ── 🚨 AN OPTIMISTIC UPDATE MUST NOT OUTLIVE A FAILED WRITE ──
//
// Every mutator here updates memory first so the screen responds, then writes.
// When the write fails it RELOADS rather than leaving the optimistic value in
// place: on this tab the optimistic value is a grade or a review the user just
// typed, and leaving it on screen after a failed save is the app telling them
// their work is stored when it is not.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  deletePracticeSession, importPracticeSession, loadPracticeSessions,
  loadPracticeTrades, savePracticeSession, savePracticeTrade,
  type PracticeSession, type PracticeTrade,
} from '../../io/sync/practice';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface PracticeState {
  phase: 'loading' | 'ready' | 'failed';
  sessions: readonly PracticeSession[] | null;
  trades: readonly PracticeTrade[] | null;
  error: string | null;
  reload: () => void;
  /** Import selected trades as ONE new session. */
  importSession: (
    session: PracticeSession, trades: readonly PracticeTrade[],
  ) => Promise<{ error: string | null }>;
  /** Save a session's own fields — the review form, the grade, dismissed. */
  saveSession: (session: PracticeSession) => Promise<{ error: string | null }>;
  /** Save an annotation made on one copied trade. */
  saveTrade: (trade: PracticeTrade) => Promise<{ error: string | null }>;
  /** ⚠ Cascades to the session's trades. The caller must have said so. */
  removeSession: (id: string) => Promise<{ error: string | null }>;
}

const Ctx = createContext<PracticeState | null>(null);

export function usePractice(): PracticeState {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePractice() outside <PracticeProvider>');
  return v;
}

export function PracticeProvider(
  { sb, userId, children }: { sb: SupabaseClient; userId: string; children: ReactNode },
) {
  const [phase, setPhase] = useState<PracticeState['phase']>('loading');
  const [sessions, setSessions] = useState<readonly PracticeSession[] | null>(null);
  const [trades, setTrades] = useState<readonly PracticeTrade[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void (async () => {
      try {
        // ⚠ Together. See the header — a session that renders before its trades
        // would briefly read as empty.
        const [s, t] = await Promise.all([loadPracticeSessions(sb), loadPracticeTrades(sb)]);
        if (cancelled) return;
        setSessions(s);
        setTrades(t);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('practice', e));
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sb, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const importSession = useCallback(async (
    session: PracticeSession, rows: readonly PracticeTrade[],
  ) => {
    const res = await importPracticeSession(sb, userId, session, rows);
    // ⚠ RELOAD EITHER WAY. On success the server owns the truth; on failure the
    // import may be half done (the session written, its trades not), and only a
    // read can say which.
    setNonce((n) => n + 1);
    return { error: res.error };
  }, [sb, userId]);

  const saveSession = useCallback(async (session: PracticeSession) => {
    setSessions((cur) => (cur ?? []).map((s) => (s.id === session.id ? session : s)));
    const res = await savePracticeSession(sb, userId, session);
    // 🚨 The optimistic value here is a grade or a review the user just typed.
    // Leaving it on screen after a failed save tells them it is stored.
    if (res.error) setNonce((n) => n + 1);
    return { error: res.error };
  }, [sb, userId]);

  const saveTrade = useCallback(async (trade: PracticeTrade) => {
    setTrades((cur) => (cur ?? []).map((t) => (t.id === trade.id ? trade : t)));
    const res = await savePracticeTrade(sb, userId, trade);
    if (res.error) setNonce((n) => n + 1);
    return { error: res.error };
  }, [sb, userId]);

  const removeSession = useCallback(async (id: string) => {
    setSessions((cur) => (cur ?? []).filter((s) => s.id !== id));
    // ⚠ The trades go too, in memory as well as in the database — the FK
    // cascades, and leaving them here would keep a deleted session's rows in
    // every count until the next reload.
    setTrades((cur) => (cur ?? []).filter((t) => t.sessionId !== id));
    const res = await deletePracticeSession(sb, userId, id);
    if (res.error) setNonce((n) => n + 1);
    return { error: res.error };
  }, [sb, userId]);

  const value = useMemo<PracticeState>(
    () => ({
      phase, sessions, trades, error, reload,
      importSession, saveSession, saveTrade, removeSession,
    }),
    [phase, sessions, trades, error, reload, importSession, saveSession, saveTrade, removeSession],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
